// Interstitial de re-consentimiento de T&C (ADR 0008).
//
// Componente reutilizable: lo renderiza Login (flujo auth_code) y DeviceVerify
// (flujo device) cuando el backend responde consent_required. Recibe el
// consent_ticket + la info de la version vigente y resuelve via /auth/consent-accept.
//
//   - status 'grace'   → "Aceptar" + "Ahora no" (salteable).
//   - status 'expired' → solo "Aceptar" (obligatorio).
//
// El ticket vive en memoria (TTL 10min); por eso no usamos una ruta separada
// (un refresh perderia el ticket) sino render inline en cada flujo.

import { useState } from 'react';
import AuthShell from '@/components/AuthShell';
import SubmitButton from '@/components/SubmitButton';
import { consentAccept, ApiError, type TosNotice } from '@/lib/api';
import { btn } from '@/lib/button-styles';

interface ConsentTermsProps {
  partnerId: string;
  ticket: string;
  tos: TosNotice;
  onResolved: (result: { redirect_to: string } | { approved: true }) => void;
}

function errorMessageFor(code: string): string {
  switch (code) {
    case 'invalid_grant':
    case 'invalid_request_body':
      // Ticket vencido o invalido — el usuario debe re-loguear.
      return 'La sesión de aceptación expiró. Volvé a ingresar para continuar.';
    case 'rate_limited':
      return 'Demasiados intentos. Esperá un momento.';
    default:
      return 'No pudimos registrar tu aceptación. Probá de nuevo.';
  }
}

function formatDeadline(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function ConsentTerms({
  partnerId,
  ticket,
  tos,
  onResolved,
}: ConsentTermsProps): JSX.Element {
  const [loading, setLoading] = useState<'accept' | 'skip' | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const isExpired = tos.status === 'expired';
  const deadline = formatDeadline(tos.grace_deadline);

  async function resolve(action: 'accept' | 'skip'): Promise<void> {
    setLoading(action);
    setErr(null);
    try {
      const result = await consentAccept(partnerId, { consentTicket: ticket, action });
      onResolved(result);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : 'unknown';
      setErr(errorMessageFor(code));
      setLoading(null);
    }
  }

  return (
    <AuthShell
      title="Actualizamos los Términos y Condiciones"
      subtitle={
        isExpired
          ? 'Para seguir usando el servicio, necesitás aceptar la nueva versión.'
          : 'Revisá la nueva versión. Podés aceptarla ahora o más adelante.'
      }
    >
      <div className="space-y-4">
        <div className="rounded-lg border border-border p-4 text-sm text-text-muted">
          <p>
            {tos.tos_url ? (
              <>
                Leé los{' '}
                <a
                  href={tos.tos_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-text underline"
                >
                  nuevos Términos y Condiciones
                </a>
                .
              </>
            ) : (
              'Se actualizaron nuestros Términos y Condiciones.'
            )}
          </p>
          {!isExpired && deadline ? (
            <p className="mt-2 text-xs">Tenés tiempo de aceptarlos hasta el {deadline}.</p>
          ) : null}
        </div>

        {err ? (
          <div
            role="alert"
            aria-live="polite"
            className="rounded-lg border border-error/30 bg-error/10 p-3 text-sm text-error"
          >
            {err}
          </div>
        ) : null}

        <SubmitButton
          loading={loading === 'accept'}
          loadingLabel="Registrando…"
          onClick={() => void resolve('accept')}
          type="button"
        >
          Aceptar y continuar
        </SubmitButton>

        {!isExpired ? (
          <button
            type="button"
            disabled={loading !== null}
            onClick={() => void resolve('skip')}
            className={btn('secondary', 'md', 'w-full')}
          >
            Ahora no
          </button>
        ) : null}
      </div>
    </AuthShell>
  );
}
